import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface DistributionType {
  name: string;
  formula: string;
  description: string;
  color: string;
}

interface StatMethod {
  name: string;
  english: string;
  icon: string;
  color: string;
}

interface KeyConcept {
  name: string;
  symbol: string;
  description: string;
}

interface StatisticsTemplateProps {
  topic: string;
  subtitle: string;
  distributions: DistributionType[];
  methods: StatMethod[];
  concepts: KeyConcept[];
  bellCurveLabels: { label: string; position: number }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: StatisticsTemplateProps = {
  topic: '统计学与数据分析',
  subtitle: 'Statistics & Data Analysis',
  distributions: [
    {
      name: '正态分布',
      formula: 'N(μ, σ²)',
      description: '最常见的连续型概率分布，自然界现象的基石',
      color: '#14b8a6',
    },
    {
      name: '泊松分布',
      formula: 'P(λ)',
      description: '描述单位时间内随机事件发生次数的概率',
      color: '#2dd4bf',
    },
    {
      name: '均匀分布',
      formula: 'U(a, b)',
      description: '每个值出现的概率完全相同的理想分布',
      color: '#5eead4',
    },
    {
      name: '二项分布',
      formula: 'B(n, p)',
      description: 'n次独立伯努利试验中成功次数的分布',
      color: '#99f6e4',
    },
  ],
  methods: [
    { name: '描述统计', english: 'Descriptive', icon: '📊', color: '#14b8a6' },
    { name: '推断统计', english: 'Inferential', icon: '🔬', color: '#0d9488' },
    { name: '贝叶斯统计', english: 'Bayesian', icon: '🧠', color: '#2dd4bf' },
    { name: '回归分析', english: 'Regression', icon: '📈', color: '#5eead4' },
  ],
  concepts: [
    { name: 'p值', symbol: 'p', description: '假设检验中衡量证据强度的概率值' },
    { name: '置信区间', symbol: 'CI', description: '参数真值所在的估计范围' },
    { name: '标准差', symbol: 'σ', description: '数据离散程度的核心度量' },
    { name: '方差', symbol: 'σ²', description: '标准差的平方，刻画数据波动性' },
  ],
  bellCurveLabels: [
    { label: '-3σ', position: 0.05 },
    { label: '-2σ', position: 0.18 },
    { label: '-1σ', position: 0.35 },
    { label: 'μ', position: 0.5 },
    { label: '+1σ', position: 0.65 },
    { label: '+2σ', position: 0.82 },
    { label: '+3σ', position: 0.95 },
  ],
  stats: [
    { value: '68.2%', label: '数据落在 ±1σ 内' },
    { value: '95.4%', label: '数据落在 ±2σ 内' },
    { value: '99.7%', label: '数据落在 ±3σ 内' },
  ],
  bottomQuote:
    '没有数据支撑的判断只是猜测——统计学让我们在不确定性中找到确定的答案',
};

export const StatisticsTemplate: React.FC<
  Partial<StatisticsTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const distStart = 30;
  const methodStart = 160;
  const bellStart = 200;
  const conceptStart = 300;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const bellDraw = interpolate(
    frame,
    [bellStart, bellStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const bellCurvePath =
    'M30,180 Q80,178 140,170 Q200,155 260,120 Q320,60 400,18 Q480,60 540,120 Q600,155 660,170 Q720,178 770,180';

  const areaPath =
    'M30,180 Q80,178 140,170 Q200,155 260,120 Q320,60 400,18 Q480,60 540,120 Q600,155 660,170 Q720,178 770,180 L770,180 L30,180 Z';

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #042f2e 0%, #021a19 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Data dot particles */}
      {Array.from({ length: 55 }).map((_, i) => {
        const x = (i * 311) % 1920;
        const y = (i * 179) % 1080;
        const size = 2 + (i % 4);
        const drift = Math.sin(frame * 0.025 + i) * 6;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.04 + i * 0.8);
        const colors = ['#14b8a6', '#5eead4', '#2dd4bf', '#99f6e4'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 ${size * 3}px ${colors[i % colors.length]}40`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#0f766e',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#5eead4',
            flexShrink: 0,
          }}
        >
          📊 数据科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#ccfbf1',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Distribution Types ===== */}
      <div style={{ position: 'absolute', top: 105, left: 36, width: 440 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          概率分布类型
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.distributions.map((dist, i) => {
            const s = spring({
              frame: frame - (distStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  background: '#0a2420',
                  borderRadius: 14,
                  padding: '14px 18px',
                  border: `1.5px solid ${dist.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: dist.color }}>
                    {dist.name}
                  </span>
                  <span
                    style={{
                      fontSize: 22,
                      color: '#5eead4',
                      fontFamily: 'monospace',
                      fontWeight: 600,
                    }}
                  >
                    {dist.formula}
                  </span>
                </div>
                <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 4 }}>
                  {dist.description}
                </div>
              </div>
            );
          })}
        </div>

        {/* Statistical Methods */}
        <div style={{ marginTop: 22 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            统计方法
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.methods.map((m, i) => {
              const s = spring({
                frame: frame - (methodStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0a2420',
                    borderRadius: 12,
                    padding: '14px 12px',
                    border: `1px solid ${m.color}30`,
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                  }}
                >
                  <div style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>
                    {m.icon}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      minWidth: 0,
                    }}
                  >
                    <div style={{ fontSize: 20, fontWeight: 700, color: m.color }}>
                      {m.name}
                    </div>
                    <div style={{ fontSize: 20, color: '#64748b', marginTop: 2 }}>
                      {m.english}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: Bell Curve SVG ===== */}
      <div style={{ position: 'absolute', top: 105, left: 570, width: 760 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 14,
            letterSpacing: '0.08em',
          }}
        >
          正态分布曲线 · 68-95-99.7 法则
        </div>
        <svg width="783" height="297" viewBox="0 0 800 220">
          <defs>
            <linearGradient id="bellGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {[60, 100, 140, 180].map((y) => (
            <line
              key={y}
              x1="30"
              y1={y}
              x2="770"
              y2={y}
              stroke="#134e4a"
              strokeWidth="1"
              opacity="0.3"
            />
          ))}

          <path
            d={areaPath}
            fill="url(#bellGrad)"
            opacity={bellDraw}
          />

          <path
            d={bellCurvePath}
            fill="none"
            stroke="#14b8a6"
            strokeWidth="4"
            strokeDasharray="1400"
            strokeDashoffset={1400 * (1 - bellDraw)}
            strokeLinecap="round"
          />

          {/* σ region bands */}
          <rect
            x="260" y="18" width="280" height="162" rx="4"
            fill="#14b8a6" opacity={0.06 * bellDraw}
          />
          <rect
            x="140" y="18" width="520" height="162" rx="4"
            fill="#0d9488" opacity={0.04 * bellDraw}
          />

          {p.bellCurveLabels.map((bl, i) => {
            const xPos = 30 + bl.position * 740;
            const labelOp = interpolate(
              frame,
              [bellStart + 50 + i * 8, bellStart + 70 + i * 8],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            return (
              <g key={i} opacity={labelOp}>
                <line
                  x1={xPos} y1="180" x2={xPos} y2="190"
                  stroke="#5eead4" strokeWidth="2"
                />
                <text
                  x={xPos} y="208"
                  fill="#5eead4" fontSize="22" fontWeight="700"
                  textAnchor="middle"
                >
                  {bl.label}
                </text>
              </g>
            );
          })}

          {/* 68.2% annotation */}
          <g
            opacity={interpolate(
              frame,
              [bellStart + 70, bellStart + 90],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
          >
            <line x1="280" y1="40" x2="520" y2="40" stroke="#2dd4bf" strokeWidth="2" />
            <text x="400" y="34" fill="#2dd4bf" fontSize="22" fontWeight="700" textAnchor="middle">
              68.2%
            </text>
          </g>
        </svg>

        {/* Key Concepts */}
        <div style={{ marginTop: -20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            核心概念
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
            }}
          >
            {p.concepts.map((c, i) => {
              const s = spring({
                frame: frame - (conceptStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    minWidth: 0,
                    background: '#0a2420',
                    borderRadius: 14,
                    padding: '12px 14px',
                    border: '1.5px solid #134e4a',
                    opacity: s,
                    transform: `translateX(${(1 - s) * 30}px)`,
                  }}
                >
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: '#14b8a620',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 24,
                      fontWeight: 900,
                      color: '#14b8a6',
                      fontFamily: 'monospace',
                      flexShrink: 0,
                      boxShadow: '0 0 18px #14b8a630',
                    }}
                  >
                    {c.symbol}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#5eead4' }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 18, color: '#94a3b8', marginTop: 2 }}>
                      {c.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Application fields */}
        <div style={{ marginTop: 18 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            应用领域
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {[
              { icon: '🏥', label: '医学试验' },
              { icon: '💰', label: '金融分析' },
              { icon: '🔬', label: '科学研究' },
              { icon: '🤖', label: '机器学习' },
            ].map((field, i) => {
              const s = spring({
                frame: frame - (methodStart + 60 + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0a2420',
                    borderRadius: 12,
                    padding: '12px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    border: '1px solid #134e4a',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <span style={{ fontSize: 24, lineHeight: 1 }}>{field.icon}</span>
                  <span style={{ fontSize: 20, fontWeight: 700, color: '#5eead4' }}>
                    {field.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Data Visualization Preview ===== */}
      <div style={{ position: 'absolute', top: 105, right: 36, width: 448 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 12,
            letterSpacing: '0.08em',
          }}
        >
          数据可视化
        </div>
        <svg width="430" height="311" viewBox="0 0 360 260">
          {/* Bar chart visualization */}
          {[
            { x: 40, h: 80, label: 'Q1' },
            { x: 100, h: 140, label: 'Q2' },
            { x: 160, h: 110, label: 'Q3' },
            { x: 220, h: 180, label: 'Q4' },
            { x: 280, h: 150, label: 'Q5' },
          ].map((bar, i) => {
            const barIn = spring({
              frame: frame - (distStart + 60 + i * 12),
              fps,
              config: { damping: 14, stiffness: 100 },
            });
            return (
              <g key={i}>
                <rect
                  x={bar.x}
                  y={220 - bar.h * barIn}
                  width="40"
                  height={bar.h * barIn}
                  rx="4"
                  fill={`rgba(20, 184, 166, ${0.4 + i * 0.12})`}
                  stroke="#14b8a6"
                  strokeWidth="1"
                />
                <text
                  x={bar.x + 20} y="245"
                  fill="#94a3b8" fontSize="18"
                  textAnchor="middle"
                >
                  {bar.label}
                </text>
              </g>
            );
          })}
          <line x1="20" y1="220" x2="340" y2="220" stroke="#134e4a" strokeWidth="2" />
          <line x1="20" y1="40" x2="20" y2="220" stroke="#134e4a" strokeWidth="2" />
        </svg>

        {/* Scatter plot */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            散点图 · 相关性
          </div>
          <svg width="430" height="240" viewBox="0 0 360 201">
            <line x1="30" y1="170" x2="340" y2="170" stroke="#134e4a" strokeWidth="1.5" />
            <line x1="30" y1="20" x2="30" y2="170" stroke="#134e4a" strokeWidth="1.5" />

            {/* Trend line */}
            <line
              x1="40" y1="160" x2="330" y2="30"
              stroke="#0d9488" strokeWidth="2.5"
              strokeDasharray="6 3"
              opacity={interpolate(
                frame,
                [distStart + 100, distStart + 130],
                [0, 0.6],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            />

            {/* Scatter dots */}
            {[
              [60, 150], [80, 135], [110, 120], [130, 125],
              [150, 100], [170, 105], [200, 80], [220, 85],
              [240, 70], [260, 60], [280, 55], [300, 40],
              [90, 140], [180, 90], [250, 65],
            ].map(([cx, cy], i) => {
              const dotIn = spring({
                frame: frame - (distStart + 80 + i * 4),
                fps,
                config: { damping: 14, stiffness: 140 },
              });
              return (
                <circle
                  key={i}
                  cx={cx} cy={cy} r={5.5 * dotIn}
                  fill="#14b8a6"
                  opacity={0.7 * dotIn}
                />
              );
            })}

            <text x="185" y="200" fill="#64748b" fontSize="22" textAnchor="middle">
              r = 0.93 (强正相关)
            </text>
          </svg>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0a2420',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #134e4a',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{ fontSize: 30, fontWeight: 900, color: '#14b8a6' }}
              >
                {st.value}
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#0a2420',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #0f766e',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>📐</span>
          <span style={{ fontSize: 20, color: '#ccfbf1', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #0f766e, #14b8a6, #5eead4)',
        }}
      />
    </AbsoluteFill>
  );
};
